/* eslint-disable no-use-before-define */
import default_image from '@/assets/imagePlaceholder.jpeg';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import Popper from '@mui/material/Popper';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiProvider } from '@/provider/ApiProvider';
import { LocalOffer as BrandIcon } from '@mui/icons-material';

import {
  MultiSelectItem,
  MultiSelectPredefinedItem,
  SingleSelectItem,
} from '@/components/shared/SelectLists/Common';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import BrandLogoWrapper from '@/components/styled/BrandLogoWrapper';

export default function BrandSelect() {
  const [anchorEl, setAnchorEl] = useState(null);
  const popperRef = useRef(null);
  const [value, setValue] = useState([]);
  const [BrandLogos, setBrandslogos] = useState([]);
  const [pendingValue] = useState([]);
  // TODO: Create a single object for selectedBrand to contain img + name.
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedBrandName, setSelectedBrandName] = useState(null);
  const [singleBrandFlag, setSingleBrandFlag] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { secondarySidebarData } = useSidebarNavigation();
  const { setHeaderBrand } = useGlobalFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 20;

  useEffect(() => {
    const brandObject = localStorage.getItem('brand_id_object');
    brandObject && setSelectedBrandName(JSON.parse(brandObject).name);
  }, [selectedBrand]);

  useEffect(() => {
    setOffset(0);
    setHasMore(true);

    const timeoutId = setTimeout(() => {
      console.log('Searching for:', searchTerm); // Debug log
      fetchBrands(true, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (BrandLogos.length == 2) {
      const brandAccessData = localStorage.getItem('BrandAccess');

      if (brandAccessData) {
        const userBrandAccess = JSON.parse(brandAccessData);
        if (
          Array.isArray(userBrandAccess) &&
          userBrandAccess.length === 1 &&
          userBrandAccess[0] !== '*'
        ) {
          setSingleBrandFlag(true);
        }
      }
    } else {
      setSingleBrandFlag(false);
      if (localStorage.getItem('brand_id') && localStorage.getItem('brand_id_object')) {
        var selectedBrandIdObject = JSON.parse(localStorage.getItem('brand_id_object'));
        if (selectedBrandIdObject.profile_logo_path != null) {
          setSelectedBrand(selectedBrandIdObject.profile_logo_path);
        } else {
          setSelectedBrand('no image');
        }
      }
    }
  }, [BrandLogos]);

  useEffect(() => {
    // Don't fetch brands initially if we're on the brands list page
    const currentPath = location.pathname;
    if (currentPath.includes('/listview/brand') || currentPath.includes('/brandprofile')) {
      return;
    }

    // Initial load
    fetchBrands(true, ''); // Reset data, empty search
  }, [location.pathname]); // Add location.pathname as dependency

  const fetchBrands = async (resetData = false, searchQuery = '') => {
    if (isLoading || (!hasMore && !resetData)) return;

    setIsLoading(true);

    try {
      const currentOffset = resetData ? 0 : offset;
      console.log('Fetching brands with offset:', currentOffset, 'search:', searchQuery);

      const params = {
        offset: currentOffset,
        limit: limit,
      };

      // Only add term if it's not empty if its empty, we want to fetch all brands
      // This allows us to reset the search and fetch all brands again
      if (searchQuery && searchQuery.trim() !== '') {
        params.term = searchQuery;
      }

      const response = await ApiProvider.webserviceGet('Brands/GetBrands', params);

      if (response.success) {
        const newBrands = response.data.results;
        const totalCount = response.data.pagination?.total || 0;

        console.log(
          'Got',
          newBrands.length,
          'brands, total:',
          totalCount,
          'current offset:',
          currentOffset,
        );

        // Check if we have more data
        const moreDataAvailable = currentOffset + newBrands.length < totalCount;
        setHasMore(moreDataAvailable);

        if (resetData) {
          // First load or new search - replace all data
          const withClear = [
            { id: 'clear', name: 'Remove Filter', logo: '', logo_key: '' },
            ...newBrands,
          ];
          setBrandslogos(withClear);
          setOffset(newBrands.length); // Set offset to number of actual brands loaded (excluding 'clear' item)
        } else {
          // Append new brands - but check for duplicates
          setBrandslogos((prev) => {
            const existingIds = new Set(prev.map((brand) => brand.id));
            const uniqueNewBrands = newBrands.filter((brand) => !existingIds.has(brand.id));
            return [...prev, ...uniqueNewBrands];
          });
          setOffset((prev) => prev + newBrands.length); // Increment by actual number of new brands
        }
      }
    } catch (error) {
      ApiProvider.globalToastMessage(error, 5000, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = (event) => {
    //setPendingValue(value);
    setAnchorEl(event.currentTarget);
  };

  const closeDropdown = () => {
    setValue(pendingValue);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleClose = (event, reason) => {
    if (reason === 'escape') {
      closeDropdown();
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  const isInsideDropdown = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      anchorEl?.contains(target) ||
        popperRef.current?.contains(target) ||
        target.closest('.brandLogoAutocompletePopper'),
    );
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (isInsideDropdown(target)) {
        return;
      }
      closeDropdown();
    };

    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true);
    };
  }, [open, anchorEl]);

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.name,
  });

  return (
    <BrandLogoWrapper>
      {/* Brand dropdown. */}
      <div
        className={'brandLogoRootClass brandSelectorAnchor'}
        data-selector-anchor='brand'
        style={{ width: '100%' }}
      >
        {/* If we don't have predefined + can choose. */}
        {Boolean(selectedBrand == null) && singleBrandFlag == false && (
          <MultiSelectItem
            icon={BrandIcon}
            handleClick={handleClick}
            id={id}
            label='Select brand'
            entityLabel='Brand'
          />
        )}

        {/* If we have predefined + can choose. */}
        {Boolean(selectedBrand != null) && singleBrandFlag == false && (
          <MultiSelectPredefinedItem
            handleClick={handleClick}
            imgSrc={selectedBrand !== 'no image' ? selectedBrand : default_image}
            imgAlt={'Brand logo'}
            selectedItemName={selectedBrandName}
            entityLabel='Brand'
          />
        )}

        {/* If we cant choose predefined. */}
        {singleBrandFlag && (
          <SingleSelectItem
            imgSrc={selectedBrand !== 'no image' ? selectedBrand : default_image}
            imgAlt={'Brand logo'}
            selectedItemName={selectedBrandName}
            entityLabel='Brand'
          />
        )}
      </div>
      <Popper
        ref={popperRef}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        className={`brandLogoclassesPopper brandSelectPopper ${open ? 'selectorDropdownOpen' : ''}`}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        sx={{ left: '65px !important', zIndex: 1700 }}
      >
        <div className={'brandLogoClassesHeader'}>Select Brand</div>
        <div
          // The native "scroll" event does not bubble, but MUI's Autocomplete
          // listbox (the element that actually scrolls) is a descendant of
          // this div, so we must listen on the capture phase to detect it.
          onScrollCapture={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;

            if (isNearBottom && hasMore && !isLoading) {
              fetchBrands(false, searchTerm); // Don't reset data, use current search term
            }
          }}
          style={{ maxHeight: '400px', overflowY: 'auto' }}
        >
          <Autocomplete
            open
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setSearchTerm(newInputValue);
              }
            }}
            onClose={handleClose}
            onChange={async (event, newValue) => {
              if (!newValue) {
                return;
              }
              const isClear = newValue.id == 'clear';
              const applied = await setHeaderBrand(isClear ? null : newValue);
              closeDropdown();
              if (!applied) {
                return;
              }
              if (!isClear) {
                setSelectedBrand(newValue.profile_logo_path || 'no image');
                setSelectedBrandName(newValue.name);
                const href = window.location.href;
                if (href.indexOf('propertyprofile') != -1) {
                  const href1 = href.split('#/');
                  const href2 = href1[1].split('/');
                  const activeId = secondarySidebarData?.links?.find((item) => item.isActive)?.id;
                  if (activeId) {
                    navigate('/' + href2[0] + '/' + href2[1] + '/' + activeId);
                  }
                }
                if (href.indexOf('partnerProfile') != -1) {
                  if (localStorage.getItem('partnerProfileActiveTab')) {
                    localStorage.setItem(
                      'partnerPageTab',
                      localStorage.getItem('partnerProfileActiveTab'),
                    );
                  }
                }
              } else {
                setSelectedBrand(null);
                setSelectedBrandName(null);
              }

              const serch_index = pathname.search('brandprofile');
              if (serch_index >= 0 && !isClear) {
                navigate('/brandprofile/' + newValue.id);
              }
            }}
            getOptionLabel={() => ''}
            disableCloseOnSelect
            disablePortal
            slotProps={{
              popper: {
                className: 'brandLogoAutocompletePopper',
                sx: { zIndex: 1700 },
                onPointerDown: (event) => event.stopPropagation(),
                onMouseDown: (event) => event.stopPropagation(),
                onTouchStart: (event) => event.stopPropagation(),
                onClick: (event) => event.stopPropagation(),
              },
              listbox: {
                onPointerDown: (event) => event.stopPropagation(),
                onMouseDown: (event) => event.stopPropagation(),
                onTouchStart: (event) => event.stopPropagation(),
                onClick: (event) => event.stopPropagation(),
              },
              paper: {
                onPointerDown: (event) => event.stopPropagation(),
                onMouseDown: (event) => event.stopPropagation(),
                onTouchStart: (event) => event.stopPropagation(),
                onClick: (event) => event.stopPropagation(),
              },
            }}
            renderTags={() => null}
            noOptionsText='No Brands'
            renderOption={(props, option, { index }) => {
              const isLast = index === BrandLogos.length - 1;

              const lastOptionRef = (el) => {
                if (el && isLast && hasMore && !isLoading) {
                  const observer = new IntersectionObserver(
                    ([entry]) => {
                      if (entry.isIntersecting) {
                        fetchBrands(false, searchTerm); // Load more data with current search term
                        observer.disconnect();
                      }
                    },
                    { threshold: 0.1 },
                  );
                  observer.observe(el);
                }
              };

              return (
                <li {...props} key={option.id} ref={isLast ? lastOptionRef : null}>
                  <div style={{ display: 'flex', alignItems: 'center' }} className={'skksee'}>
                    <span>
                      <img
                        src={option.profile_logo_path || default_image}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = default_image;
                        }}
                        style={{
                          width: '50px',
                          height: '35px',
                          objectFit: 'contain',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        }}
                      />
                    </span>
                    <div className={'brandLogoClassesText'}>{option.name}</div>
                  </div>
                </li>
              );
            }}
            options={[...BrandLogos].sort((a, b) => {
              // Display the selected labels first.
              let ai = value.indexOf(a);
              ai = ai == -1 ? value.length + BrandLogos.indexOf(a) : ai;
              let bi = value.indexOf(b);
              bi = bi == -1 ? value.length + BrandLogos.indexOf(b) : bi;
              return ai - bi;
            })}
            filterOptions={filterOptions}
            renderInput={(params) => (
              <InputBase
                ref={params.InputProps.ref}
                inputProps={params.inputProps}
                autoFocus
                className={'BrandLogoClassesInputBase'}
              />
            )}
          />
        </div>
        {isLoading && (
          <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
            Loading more brands...
          </div>
        )}
      </Popper>
    </BrandLogoWrapper>
  );
}
